import { z } from 'zod';
import { paginationQuery } from '../api.js';
/**
 * Auftragsbuch für die Personenbeförderung mit Mietwagen (Schlüssel `ridebook`, Änderungswunsch vom 17.10.2026, siehe docs/auftragsbuch.md).
 *
 * Rechtsgrundlage ist § 49 Abs. 4 PBefG: Mit Mietwagen dürfen nur Aufträge ausgeführt werden, die am Betriebssitz oder in der Wohnung des Unternehmers eingegangen sind. Den
 * Eingang des Auftrags erfasst der Unternehmer buchmäßig oder elektronisch (auch mittels appbasierten Systems) und bewahrt die Aufzeichnung ein Jahr auf (Satz 4). Die Behörden
 * (in München das Kreisverwaltungsreferat) verlangen ein Auftragseingangsbuch, bezogen auf den Mietwagen, vollständig, unveränderlich, am Betriebssitz geführt und stets bis zur
 * letzten Fahrt aktuell. Steuerlich kommen die Einzelaufzeichnungspflicht und die GoBD dazu.
 *
 * Das Buch ist eine einzige, nur fortschreibbare Kette von Einträgen. Jeder Eintrag ist eine Tatsache zu einem Auftrag zu einem Zeitpunkt, nie eine Änderung:
 * Auftragseingang, Weitergabe an den Fahrer, Fahrtbeginn, Fahrtende, Stornierung und Berichtigung. So bleibt alles unveränderlich, auch wenn sich ein Auftrag im Lauf entwickelt.
 * Ein bestätigter Eintrag trägt eine durchlaufende Nummer, den Zeitpunkt der Erfassung und einen Hash, in den der Hash des Vorgängers eingeht. Wer einen Eintrag ändert, löscht oder
 * einschiebt, bricht die Kette, und die Prüfung zeigt es.
 */
export const RIDEBOOK_KINDS = ['received', 'assigned', 'started', 'finished', 'cancelled', 'correction'];
export const RIDEBOOK_KIND_LABELS = {
    received: 'Auftragseingang',
    assigned: 'Weitergabe an den Fahrer',
    started: 'Fahrtbeginn',
    finished: 'Fahrtende',
    cancelled: 'Auftrag nicht ausgeführt',
    correction: 'Berichtigung',
};
/** draft: Entwurf, noch änderbar und löschbar. final: bestätigt, im Buch, unveränderlich. */
export const RIDEBOOK_STATUSES = ['draft', 'final'];
export const RIDEBOOK_STATUS_LABELS = { draft: 'Entwurf', final: 'Im Buch' };
/** auto: aus Wirkia erfasst (Anfrage, Auftrag, Planung). manual: von Hand eingetragen. */
export const RIDEBOOK_SOURCES = ['auto', 'manual'];
export const RIDEBOOK_SOURCE_LABELS = { auto: 'Automatisch erfasst', manual: 'Von Hand eingetragen' };
/** Auf welchem Weg der Auftrag am Betriebssitz eingegangen ist */
export const RIDEBOOK_CHANNELS = ['phone', 'email', 'website', 'app', 'counter', 'other'];
export const RIDEBOOK_CHANNEL_LABELS = { phone: 'Telefon', email: 'E-Mail', website: 'Webseite', app: 'App', counter: 'Persönlich am Betriebssitz', other: 'Anders' };
export const RIDEBOOK_PAYMENT_METHODS = ['cash', 'card', 'transfer', 'invoice', 'other'];
export const RIDEBOOK_PAYMENT_LABELS = { cash: 'Bar', card: 'Karte', transfer: 'Überweisung', invoice: 'Auf Rechnung', other: 'Anders' };
/** Aufbewahrung in Jahren: das PBefG verlangt ein Jahr, steuerlich gelten längere Fristen (Aufzeichnungen zu Beförderungsaufträgen), deshalb acht */
export const RIDEBOOK_RETENTION_YEARS = 8;
/** Ein Eintrag, der später als so viele Minuten nach dem Zeitpunkt, den er beschreibt, ins Buch kommt, gilt als nachträglich erfasst und wird so gekennzeichnet */
export const RIDEBOOK_LATE_MINUTES = 15;
// ---------------------------------------------------------------------------
// Inhalte der Einträge (Nutzdaten je Art). Zeitpunkte als ISO-8601 mit Zeitzone.
// ---------------------------------------------------------------------------
const text = (label, min, max) => z.string().trim().min(min, `${label} fehlt`).max(max, `${label} ist zu lang`).refine((v) => !/[<>]/.test(v), `${label}: Bitte keine Sonderzeichen wie < und >`);
const optText = (max) => z.string().trim().max(max).refine((v) => !/[<>]/.test(v), 'Bitte keine Sonderzeichen wie < und >').optional().transform((v) => (v ? v : undefined));
const moment = (label) => z
    .string(`${label} fehlt`)
    .refine((v) => !Number.isNaN(Date.parse(v)) && /T\d{2}:\d{2}/.test(v), `${label}: Bitte Datum und Uhrzeit angeben`)
    .transform((v) => new Date(v).toISOString());
const cents = z.number('Bitte einen Betrag angeben').int('Betrag in Cent').min(0, 'Nicht negativ').max(10_000_000);
export const receivedPayload = z
    .object({
    /** Wann der Auftrag am Betriebssitz eingegangen ist */
    receivedAt: moment('Auftragseingang'),
    receivedVia: z.enum(RIDEBOOK_CHANNELS, 'Bitte den Eingangsweg wählen'),
    passengerName: text('Name des Fahrgasts', 2, 120),
    passengerPhone: optText(40),
    pickupAddress: text('Abholort', 3, 300),
    destinationAddress: text('Zielort', 3, 300),
    /** Gewünschter Beginn der Fahrt */
    pickupAt: moment('Beginn der Fahrt'),
    passengers: z.number().int().min(1).max(50).optional(),
    /** Beauftragte Strecke in Kilometern, soweit bekannt (zum Beispiel für Mindestpreise der Städte) */
    orderedKm: z.number().min(0).max(5000).optional(),
    agreedPriceCents: cents.optional(),
    /** Wann die Anfrage eingegangen ist, aus der der Auftrag entstand (nur Information, der Auftrag gilt ab `receivedAt`) */
    requestReceivedAt: moment('Eingang der Anfrage').optional(),
    note: optText(500),
})
    .strict();
export const assignedPayload = z
    .object({
    /** Wann der Auftrag an den Fahrer übermittelt wurde */
    assignedAt: moment('Zeitpunkt der Weitergabe'),
    /** Fahrzeug und Fahrer aus Wirkia, falls es sie dort gibt. Ohne das Modul Flotte tragen Kennzeichen und Name allein. */
    vehicleId: z.uuid().optional(),
    /** Amtliches Kennzeichen zum Zeitpunkt der Weitergabe (Schnappschuss) */
    plate: text('Kennzeichen', 2, 20),
    driverId: z.uuid().optional(),
    /** Name des Fahrers zum Zeitpunkt der Weitergabe (Schnappschuss) */
    driverName: text('Name des Fahrers', 2, 120),
    /** Standort des Fahrzeugs bei der Weitergabe, bei Vermittlung über eine App */
    vehiclePosition: optText(300),
})
    .strict();
export const startedPayload = z.object({ startedAt: moment('Fahrtbeginn'), startPlace: optText(300), odometerKm: z.number().int().min(0).max(5_000_000).optional() }).strict();
export const finishedPayload = z
    .object({
    finishedAt: moment('Fahrtende'),
    endPlace: optText(300),
    distanceKm: z.number().min(0).max(5000).optional(),
    odometerKm: z.number().int().min(0).max(5_000_000).optional(),
    /** Berechneter Fahrpreis brutto in Cent */
    priceCents: cents.optional(),
    paymentMethod: z.enum(RIDEBOOK_PAYMENT_METHODS).optional(),
})
    .strict();
export const cancelledPayload = z.object({ cancelledAt: moment('Zeitpunkt'), reason: text('Grund', 3, 500) }).strict();
/** Berichtigung: Der ursprüngliche Eintrag bleibt stehen, die Berichtigung nennt, was richtig ist und warum */
export const correctionPayload = z.object({ text: text('Berichtigung', 3, 1000), correctsSeq: z.number().int().min(1).optional() }).strict();
const PAYLOADS = { received: receivedPayload, assigned: assignedPayload, started: startedPayload, finished: finishedPayload, cancelled: cancelledPayload, correction: correctionPayload };
// ---------------------------------------------------------------------------
// Eingaben der Schnittstelle
// ---------------------------------------------------------------------------
/**
 * Neuer Entwurf. Ein Eintrag zu einem bestehenden Auftrag nennt den Auftragseingang (`refId`), zu dem er gehört. Ein Entwurf darf unvollständig sein, die Prüfung aller Angaben
 * (`parsePayload`) geschieht erst mit der Bestätigung.
 */
export const createDraftInput = z
    .object({
    kind: z.enum(RIDEBOOK_KINDS),
    refId: z.uuid('Ungültiger Auftrag').optional(),
    payload: z.record(z.string(), z.unknown()),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.kind !== 'received' && !v.refId)
        ctx.addIssue({ code: 'custom', path: ['refId'], message: 'Bitte den Auftrag wählen, zu dem der Eintrag gehört' });
    if (v.kind === 'received' && v.refId)
        ctx.addIssue({ code: 'custom', path: ['refId'], message: 'Ein Auftragseingang gehört zu keinem früheren Auftrag' });
    if (JSON.stringify(v.payload).length > 8000)
        ctx.addIssue({ code: 'custom', path: ['payload'], message: 'Die Angaben sind zu lang' });
});
export const updateDraftInput = z.object({ payload: z.record(z.string(), z.unknown()) }).strict();
/** Prüft die Nutzdaten einer Art und gibt die bereinigte Fassung zurück, oder die Meldungen je Feld */
export function parsePayload(kind, payload) {
    const r = PAYLOADS[kind].safeParse(payload);
    if (r.success)
        return { ok: true, data: r.data };
    const fields = {};
    for (const i of r.error.issues)
        fields[`payload.${i.path.join('.')}`] ??= i.message;
    return { ok: false, fields };
}
export const listRidebookQuery = paginationQuery.extend({
    status: z.enum(RIDEBOOK_STATUSES).optional(),
    kind: z.enum(RIDEBOOK_KINDS).optional(),
    plate: z.string().trim().max(20).optional(),
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
    q: z.string().trim().max(100).optional(),
    /** Nur Aufträge (Auftragseingänge) zeigen, nicht die einzelnen Ereignisse */
    ordersOnly: z.enum(['true', 'false']).optional(),
});
export const exportRidebookQuery = z.object({ from: z.iso.date().optional(), to: z.iso.date().optional(), plate: z.string().trim().max(20).optional() });
/**
 * Bezeichnungen, unter denen ein Anfragetyp die Fahrtdaten führen kann (Schlüssel der Felder, ohne Beachtung von Groß- und Kleinschreibung und von Trennzeichen). Daraus entsteht der
 * Auftragseingang automatisch, wenn der Unternehmer einen Auftrag annimmt. Hat die Anfrage kein solches Feld, legt Wirkia einen Entwurf an, den der Unternehmer ergänzt.
 */
export const RIDEBOOK_FIELD_ALIASES = {
    pickupAddress: ['pickup', 'pickupaddress', 'fromaddress', 'startaddress', 'pickupplace', 'abholort', 'abholadresse', 'abholung', 'start', 'startort', 'von', 'from'],
    destinationAddress: ['destination', 'destinationaddress', 'toaddress', 'dropoff', 'zielort', 'zieladresse', 'ziel', 'nach', 'to'],
    pickupAt: ['pickupat', 'pickuptime', 'abholzeit', 'abholzeitpunkt', 'abfahrt', 'abfahrtszeit', 'fahrtbeginn'],
    pickupTime: ['uhrzeit', 'zeit', 'time', 'abholuhrzeit'],
    passengers: ['passengers', 'personen', 'fahrgaeste', 'anzahlpersonen', 'anzahlfahrgaeste'],
    orderedKm: ['km', 'kilometer', 'distance', 'strecke', 'orderedkm'],
};
//# sourceMappingURL=ridebook.js.map